// ============================================================================
// @insio/validation - Issue Code Registry
// Single source of truth: each code is defined exactly once, with its
// description, inside the subset of the document it belongs to. The registry
// models (per document and the union) are derived from the definitions.
// ============================================================================

/** Codes shared by every document type — party checks, AI analysis and the engine itself. */
const COMMON_ISSUE_CODE_DEFINITIONS = {
  // ── Party ───────────────────────────────────────────────────────────────
  partyDuplicate: 'Party is a duplicate of an existing party',
  partyMayBeDuplicate: 'Party may be a duplicate of an existing party',
  ARESLookupOtherVatIdFound: 'ARES lookup found a different VAT ID for this company',

  // ── ID format validation ─────────────────────────────────────────────────
  vatIdInvalidFormat: 'VAT ID has an invalid format or could not be verified',
  companyIdInvalidFormat: 'Company ID (IČO) has an invalid format',
  taxIdInvalidFormat: 'Tax ID has an invalid format',

  // ── AI analysis ──────────────────────────────────────────────────────────
  aiAnalysisError: 'AI analysis failed to execute or parse its response',
  aiAnalysisIssue: 'Finding that does not match any specific code',

  // ── Engine internal ──────────────────────────────────────────────────────
  ruleException: 'A validation rule threw an unexpected exception'
} as const;

/** Codes of document lines, shared by every document that carries lines. */
const LINE_ISSUE_CODE_DEFINITIONS = {
  noLines: 'No line items found on the document',
  lineDescriptionMissing: 'Line item description is missing',
  lineQuantityMissing: 'Line item quantity is missing',
  lineUnitPriceMissing: 'Line item unit price is missing'
} as const;

/** Codes about the supplier named on a document, shared by invoices and purchase orders. */
const SUPPLIER_ISSUE_CODE_DEFINITIONS = {
  supplierMissing: 'Supplier is missing',
  supplierNotLinked: 'Supplier is not linked to a party from the address book',
  supplierNameMissing: 'Supplier name is missing',
  supplierIdentificationMissing: 'Supplier identification is missing or invalid'
} as const;

/** Codes about the contract a document is linked to, shared by every procurement document. */
const CONTRACT_ISSUE_CODE_DEFINITIONS = {
  contractNotInForce: 'The document date falls in no term of the linked contract, or after it ends',
  supplierNotOnContract: 'Supplier on the document is not the party of the linked contract',
  contractCurrencyMismatch: 'Document currency differs from the currency of the linked contract'
} as const;

/** Codes about the cost coding of a linked contract, shared by requisitions and purchase orders. */
const CONTRACT_COST_CODING_ISSUE_CODE_DEFINITIONS = {
  contractCostCenterMismatch:
    'Cost centre of the document differs from that of the linked contract',
  contractJobMismatch: 'Job of the document differs from that of the linked contract'
} as const;

/** Codes about the limit of a contract term, shared by invoices and purchase orders. */
const CONTRACT_LIMIT_ISSUE_CODE_DEFINITIONS = {
  contractLimitExceeded: 'The document exceeds what remains of the contract limit'
} as const;

/** Codes only an invoice or receipt can raise. */
const INVOICE_ISSUE_CODE_DEFINITIONS = {
  // ── Document type ───────────────────────────────────────────────────────
  documentTypeMissing: 'Document type is missing',
  documentTypeInvalid: 'Document type is invalid',

  // ── Essential invoice fields ─────────────────────────────────────────────
  invoiceNumberMissing: 'Invoice number is missing',
  invoiceDateMissing: 'Invoice issue date is missing or invalid',
  taxDateMissing: 'Tax (VAT) date is missing',
  invoiceDateDueDateLogicError: 'Due date is before the issue date, or the date logic is broken',
  currencyCodeMissing: 'Currency code is missing or inconsistent across the document',
  relatedDocumentReferenceMissing: 'Reference to the related document is missing',

  // ── Supplier identification ───────────────────────────────────────────────
  supplierCompanyIdMissing: 'Supplier company ID (IČO) is missing',
  supplierVatIdMissing: 'Supplier VAT ID (DIČ) is missing',
  supplierVatIdOrTaxIdRequired: 'Supplier must have a VAT ID or a tax ID',

  // ── Supplier address ─────────────────────────────────────────────────────
  supplierAddressMissing: 'Supplier address is missing',
  supplierStreetMissing: 'Supplier street is missing',
  supplierBuildingNumberMissing: 'Supplier building number is missing',
  supplierCityMissing: 'Supplier city is missing',
  supplierPostalCodeMissing: 'Supplier postal code is missing',
  supplierCountryCodeMissing: 'Supplier country code is missing',

  // ── Customer identification ──────────────────────────────────────────────
  customerNameMissing: 'Customer name is missing',
  customerIdentificationMissing: 'Customer identification is missing or invalid',
  customerCompanyIdMissing: 'Customer company ID (IČO) is missing',
  customerVatIdMissing: 'Customer VAT ID (DIČ) is missing',
  customerVatIdOrTaxIdRequired: 'Customer must have a VAT ID or a tax ID',

  // ── Customer address ─────────────────────────────────────────────────────
  customerAddressMissing: 'Customer address is missing',
  customerStreetMissing: 'Customer street is missing',
  customerBuildingNumberMissing: 'Customer building number is missing',
  customerCityMissing: 'Customer city is missing',
  customerPostalCodeMissing: 'Customer postal code is missing',
  customerCountryCodeMissing: 'Customer country code is missing',

  // ── Delivery address ─────────────────────────────────────────────────────
  deliveryStreetMissing: 'Delivery address street is missing',
  deliveryBuildingNumberMissing: 'Delivery address building number is missing',
  deliveryCityMissing: 'Delivery address city is missing',
  deliveryPostalCodeMissing: 'Delivery address postal code is missing',
  deliveryCountryCodeMissing: 'Delivery address country code is missing',

  // ── Banking details ──────────────────────────────────────────────────────
  paymentMethodMissing: 'Payment method is missing',
  paymentDueDateMissing: 'Payment due date is missing or invalid',
  bankingIbanInvalidFormat: 'IBAN has an invalid format',
  bankingAccountInvalidFormat: 'Bank account number has an invalid format',
  bankingBankCodeUnknown: 'Bank code is unknown',
  bankingDetailsMissing: 'Banking details are missing',
  bankingVariableSymbolInvalidFormat: 'Variable symbol has an invalid format',
  bankingConstantSymbolInvalidFormat: 'Constant symbol has an invalid format',
  bankingSpecificSymbolInvalidFormat: 'Specific symbol has an invalid format',
  swiftInvalid: 'SWIFT/BIC code is invalid',

  // ── Invoice totals ───────────────────────────────────────────────────────
  totalAmountMismatch: 'Total amount does not match subtotal + VAT',
  totalAmountNoVatMismatch: 'Subtotal (excl. VAT) does not match the sum of line items',
  totalVatAmountMismatch: 'Total VAT amount does not match (subtotal × VAT rate)',
  totalAmountNoVatMissing: 'Total amount excluding VAT is missing',
  totalAmountInclVatMissing: 'Total amount including VAT is missing',
  totalAmountLessThanNoVat: 'Total including VAT is less than the total excluding VAT',

  // ── Foreign currency ─────────────────────────────────────────────────────
  accountingExchangeRateMissing: 'Accounting exchange rate is missing',
  foreignCurrencyAccountingMissing:
    'Accounting-currency amounts are missing on a foreign-currency document',
  vatSummaryExchangeRateMissing: 'VAT summary exchange rate is missing',
  accountingExchangeRateDeviation:
    'Accounting exchange rate deviates by 10 % or more from the CNB/ECB reference rate',

  // ── Line items ───────────────────────────────────────────────────────────
  lineAmountNoVatMissing: 'Line item amount excluding VAT is missing',
  lineAmountInclVatMissing: 'Line item amount including VAT is missing',
  lineVatAmountMissing: 'Line item VAT amount is missing',
  lineVatRateMissing: 'Line item VAT rate is missing',
  lineAmountCalculationError: 'Line item amount does not match quantity × unit price',
  lineVatCalculationError: 'Line item VAT amount does not match its rate',
  lineTotalAmountCalculationError: 'Line item total does not match amount + VAT',
  lineNegativeSignFlip: 'Line item amounts have inconsistent signs',

  // ── VAT summary ──────────────────────────────────────────────────────────
  vatSummaryMissing: 'VAT summary is missing',
  vatSummaryItemAmountsMissing: 'VAT summary item amounts are missing',
  vatSummaryVatAmountMismatch: 'VAT summary VAT amount does not match (base × rate)',
  vatSummaryNetAmountVsLinesMismatch: 'VAT summary net amount does not match the line items',
  vatSummaryVatAmountVsLinesMismatch: 'VAT summary VAT amount does not match the line items',
  vatSummaryGroupMissingFromSummary: 'A VAT rate used by line items is missing from the summary',
  vatSummaryGroupNotFoundInLines: 'A VAT summary rate is not used by any line item',

  // ── VAT rates ────────────────────────────────────────────────────────────
  invalidVatRate: 'Non-standard or invalid VAT rate detected',

  // ── Duplicate detection ──────────────────────────────────────────────────
  duplicateInvoiceNumber: 'Potential duplicate: same invoice number already exists',
  duplicateVariableSymbol: 'Potential duplicate: same variable symbol already exists',

  // ── Supplier bank account ────────────────────────────────────────────────
  supplierBankAccountChanged:
    'Account number was not used on an earlier invoice from this supplier',
  supplierBankAccountNotPublished: "Account number is not published in the supplier's tax register",
  supplierBankAccountUnverified:
    'Account number is neither known from earlier invoices nor published by the supplier',
  supplierIbanChanged: 'IBAN was not used on an earlier invoice from this supplier',
  supplierIbanNotPublished: "IBAN is not published in the supplier's tax register",
  supplierIbanUnverified:
    'IBAN is neither known from earlier invoices nor published by the supplier',
  supplierBankAccountRegisterUnavailable:
    "Account number could not be checked against the supplier's tax register",
  supplierIbanRegisterUnavailable: "IBAN could not be checked against the supplier's tax register",
  supplierBankAccountNoneVerified: 'The supplier has no verified bank account yet',
  supplierBankAccountMismatch:
    "The printed account is none of the supplier's verified bank accounts",
  supplierIsUnreliablePayer: 'Supplier is listed as an unreliable VAT payer',

  // ── Payment state ────────────────────────────────────────────────────────
  paymentStateErpMismatch:
    'The ERP reports a different payment state than the payments recorded in Tappeer',

  // ── Matching ─────────────────────────────────────────────────────────────
  lineOrderCurrencyMismatch: 'Invoice currency differs from the currency of the matched order',
  lineOrderUnitMismatch: 'Unit of measure of a line differs from the matched order line',
  lineOrderCostCenterMismatch: 'Cost centre of a line differs from that of the matched order line',
  lineOrderJobMismatch: 'Job of a line differs from that of the matched order line',
  lineOrderUnitPriceMismatch: 'Invoiced unit price differs from the ordered price beyond tolerance',
  lineOrderOverBilled:
    'Billed quantity of a matched order line exceeds the accepted or ordered one',
  lineOrderDeliveryAlreadyBilled:
    'Other invoices already bill the whole accepted quantity of a matched order line',
  supplierNotOnOrder:
    'Supplier on the invoice differs from the supplier of a referenced or matched order',
  customerNotOnOrder:
    'Customer on the invoice differs from the customer of a referenced or matched order',

  // ── Export ───────────────────────────────────────────────────────────────
  exportFailed: 'Invoice failed during export (e.g. mapping error, missing PDF)'
} as const;

/** Codes only a requisition can raise. */
const REQUISITION_ISSUE_CODE_DEFINITIONS = {
  requisitionNumberMissing: 'Requisition number is missing',
  currencyMissing: 'Requisition currency is missing',
  orgUnitMissing: 'Organisation unit is missing while the approver is found from it',
  suggestedApproverMissing: 'Suggested approver is missing while the approver is taken from it'
} as const;

/** Codes only a purchase order can raise. */
const PURCHASE_ORDER_ISSUE_CODE_DEFINITIONS = {
  purchaseOrderNumberMissing: 'Purchase order number is missing',
  orgUnitMissing: 'Organisation unit is missing while the approver is found from it',
  suggestedApproverMissing: 'Suggested approver is missing while the approver is taken from it',

  // ── Matching ─────────────────────────────────────────────────────────────
  lineInvoiceCurrencyMismatch: 'Currency of a matched invoice differs from the order currency',
  lineInvoiceUnitMismatch: 'A linked invoice bills a line in a different unit of measure',
  lineInvoiceCostCenterMismatch: 'A linked invoice charges a line to a different cost centre',
  lineInvoiceJobMismatch: 'A linked invoice charges a line to a different job',
  lineInvoiceUnitPriceMismatch:
    'A linked invoice bills a unit price that differs from the ordered one beyond tolerance',
  lineInvoiceOverBilled: 'Billed quantity of a line exceeds the accepted or ordered one',
  lineInvoiceDeliveryAlreadyBilled:
    'Other invoices already bill the whole accepted quantity of a matched line',
  invoiceSupplierOutsideOrder: 'A matched invoice names a supplier other than the order supplier',
  invoiceCustomerOutsideOrder: 'A matched invoice names a customer other than the order customer'
} as const;

/** Every code an invoice validation may emit. */
const INVOICE_ISSUE_CODES_DEFINITIONS = {
  ...COMMON_ISSUE_CODE_DEFINITIONS,
  ...LINE_ISSUE_CODE_DEFINITIONS,
  ...SUPPLIER_ISSUE_CODE_DEFINITIONS,
  ...CONTRACT_ISSUE_CODE_DEFINITIONS,
  ...CONTRACT_LIMIT_ISSUE_CODE_DEFINITIONS,
  ...INVOICE_ISSUE_CODE_DEFINITIONS
} as const;

/** Every code a purchase order validation may emit. */
const PURCHASE_ORDER_ISSUE_CODES_DEFINITIONS = {
  ...COMMON_ISSUE_CODE_DEFINITIONS,
  ...LINE_ISSUE_CODE_DEFINITIONS,
  ...SUPPLIER_ISSUE_CODE_DEFINITIONS,
  ...CONTRACT_ISSUE_CODE_DEFINITIONS,
  ...CONTRACT_LIMIT_ISSUE_CODE_DEFINITIONS,
  ...CONTRACT_COST_CODING_ISSUE_CODE_DEFINITIONS,
  ...PURCHASE_ORDER_ISSUE_CODE_DEFINITIONS
} as const;

/** Every code a requisition validation may emit. */
const REQUISITION_ISSUE_CODES_DEFINITIONS = {
  ...COMMON_ISSUE_CODE_DEFINITIONS,
  ...LINE_ISSUE_CODE_DEFINITIONS,
  ...CONTRACT_ISSUE_CODE_DEFINITIONS,
  ...CONTRACT_COST_CODING_ISSUE_CODE_DEFINITIONS,
  ...REQUISITION_ISSUE_CODE_DEFINITIONS
} as const;

/** Every machine-readable code of `BaseIssue.code`, across all document types. */
const ISSUE_CODE_DEFINITIONS = {
  ...INVOICE_ISSUE_CODES_DEFINITIONS,
  ...REQUISITION_ISSUE_CODES_DEFINITIONS,
  ...PURCHASE_ORDER_ISSUE_CODES_DEFINITIONS
} as const;

export type IssueCode = keyof typeof ISSUE_CODE_DEFINITIONS;
export type InvoiceIssueCode = keyof typeof INVOICE_ISSUE_CODES_DEFINITIONS;
export type RequisitionIssueCode = keyof typeof REQUISITION_ISSUE_CODES_DEFINITIONS;
export type PurchaseOrderIssueCode = keyof typeof PURCHASE_ORDER_ISSUE_CODES_DEFINITIONS;

/** One registry entry — issue code with its metadata. */
export interface IssueCodeDefinition<K extends IssueCode = IssueCode> {
  readonly code: K;
  readonly description: string;
}

type IssueCodeRegistry<K extends IssueCode> = { readonly [C in K]: IssueCodeDefinition<C> };

function toRegistry<K extends IssueCode>(
  definitions: Readonly<Record<K, string>>
): IssueCodeRegistry<K> {
  return Object.fromEntries(
    Object.entries(definitions).map(([code, description]) => [code, { code, description }])
    // Safe: entries are built 1:1 from the definitions — key, code and description stay aligned.
  ) as IssueCodeRegistry<K>;
}

/** Registry model — `IssueCodes.invalidVatRate = { code: 'invalidVatRate', description: '…' }`. */
export const IssueCodes = toRegistry(ISSUE_CODE_DEFINITIONS);

/** Invoice subset of the registry — what invoice rules and the AI invoice analysis may emit. */
export const InvoiceIssueCodes = toRegistry(INVOICE_ISSUE_CODES_DEFINITIONS);

/** Requisition subset of the registry — what requisition rules may emit. */
export const RequisitionIssueCodes = toRegistry(REQUISITION_ISSUE_CODES_DEFINITIONS);

/** Purchase order subset of the registry — what purchase order rules and the ARES check may emit. */
export const PurchaseOrderIssueCodes = toRegistry(PURCHASE_ORDER_ISSUE_CODES_DEFINITIONS);
